import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { hasAnyRole, isWallDisplayOnly, type Role } from "./roles";
import { PageSkeleton } from "../components/Skeleton";

/**
 * Route gate. Sends people who are not signed in to the sign-in page with a return path, and people without the role
 * to the not-authorised page. This decides what is SHOWN; the API enforces access regardless.
 */
export function ProtectedRoute({ allow, children }: { allow: readonly Role[]; children: ReactNode }) {
  const { principal, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageSkeleton />;
  if (!principal) return <Navigate to="/sign-in" replace state={{ returnTo: `${location.pathname}${location.search}` }} />;
  // Signed in from the directory with no Operations role yet: ask for one rather than show an empty shell.
  if (principal.roles.length === 0) return <Navigate to="/request-access" replace />;
  // The kiosk principal reaches exactly one aggregated board endpoint — never an application page.
  if (isWallDisplayOnly(principal.roles)) return <Navigate to="/display" replace />;
  // Admin / Operations Manager / Planner: this sign-in must pass the authenticator step-up first (Q-O12).
  if (principal.needsStepUp) return <Navigate to="/verify" replace state={{ returnTo: `${location.pathname}${location.search}` }} />;
  if (!hasAnyRole(principal.roles, allow)) return <Navigate to="/not-authorized" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}
